import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useAuth } from "../../contexts/AuthContext";
import { validateEmail } from "../../utils/validators";
import Button from "../common/Button";
import FormInput from "../common/Input";

function AccessAccount() {
 
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const { sendMagicLink, completeMagicLinkSignIn, currentUser } = useAuth();
  const [isMagicLinkSent, setIsMagicLinkSent] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    const verifyMagicLink = async () => {
      // Check if this is a magic link verification attempt
      const urlParams = new URLSearchParams(window.location.search);
      const oobCode = urlParams.get("oobCode");

      if (oobCode) {
        setIsVerifying(true);
        try {
          const user = await completeMagicLinkSignIn();
          if (user) {
            //remove oobCode from URL
            window.history.replaceState(
              {},
              document.title,
              window.location.pathname
            );
          }
        } catch (error) {
          // Only show error if it's not the initial auth/invalid-action-code
          if (error.code !== "auth/invalid-action-code") {
            toast.error(error.message);
            setError(error.message);
          }
        } finally {
          setIsVerifying(false);
        }
      }
    };
    if (!currentUser) {
      verifyMagicLink();
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!validateEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setIsLoading(true);
    const loadingToast = toast.loading("Sending magic link...");

    try {
      const result = await sendMagicLink(email);
      toast.dismiss(loadingToast);
      toast.success(
        result.isNewAdmin
          ? "Magic link sent! You'll be set up as the first admin."
          : "Magic link sent! Check your email to continue."
      );
      setIsMagicLinkSent(true);
    } catch (error) {
      toast.dismiss(loadingToast);
      setError(error.message || "Failed to send magic link");
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="container mx-auto flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full w-full md:w-4/12">
          <div className="text-center">
            <h2 className="mt-6 text-2xl font-semibold text-gray-900">
              Verifying your login...
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Please wait while we complete your sign in
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full w-full md:w-4/12">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Admin Access Dashboard
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Only administrators can access this dashboard
        </p>
      </div>
      <div className="mt-8 sm:mx-auto w-full md:w-1/2">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {isMagicLinkSent ? (
            <div className="text-center space-y-4">
              <h3 className="text-lg font-medium text-gray-900">
                Check your email
              </h3>
              <p className="text-gray-600">
                We've sent a magic link to {email}. Click the link in your email
                to sign in.
              </p>
              <Button
                variant="secondary"
                onClick={() => {
                  setIsMagicLinkSent(false);
                  setEmail("");
                }}
              >
                Use a different email
              </Button>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="rounded-md p-2 min-h-10">
                  <div className="text-sm text-red-700">{error}</div>
                </div>
              )}
              <FormInput
                label="Email Address"
                value={email}
                className="w-full"
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your work email"
              />
              <div className="text-center">
                <Button variant="primary" loading={isLoading} type="submit">
                  {isLoading ? "Loading..." : "Send Magic Link"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default AccessAccount;
